// M2R.5 — L2SetupModal: NV lần đầu thiết lập TOTP L2.
// Wizard 2 bước:
//   Bước 1: show QR + secret base32 (copy) — NV scan bằng Google Authenticator / Authy
//   Bước 2: NV nhập code 6 số
//           → POST /auth/l2/enable (commit secret, enable)
//           → sau đó tự POST /auth/l2/verify để mở luôn session 4h
//           → setL2Session + onSuccess
//
// AC-AUTH-5 (thiết lập L2 lần đầu): lần đầu setup xong mở luôn phiên, NV không
// phải nhập lại code ngay sau đó.
//
// M115 (2026-04-27) — Fix UX:
//   1) Auto-submit khi NV nhập đủ 6 số (giống L2OtpModal/login) — dùng TotpInput.
//   2) Race: nếu enable() OK nhưng verify() fail 401 vì TOTP window đã roll
//      → secret đã commit, chỉ thiếu verify. Lần sau bypass enable, gọi thẳng
//      verify với code mới. Nếu enable trả L2_ALREADY_ENABLED (NV bấm 2 lần
//      hoặc verify fail trước đó), cũng coi như "đã enable" và verify tiếp.
//
// M117 (2026-04-27) — Loại bỏ verify() sau enable():
//   BE /auth/l2/enable nay trả luôn { l2_session, expires_at, employee } —
//   không phải gọi verify nữa. Triệt tiêu race window-roll: khi NV nhập 6 số,
//   FE chỉ gọi 1 endpoint duy nhất, hoặc thành công + mở session, hoặc fail
//   thẳng (mã sai → cảnh báo "mã chưa đúng"). Fallback: nếu enable trả
//   L2_ALREADY_ENABLED (legacy/NV bấm 2 lần) thì rơi về verify().
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { useAuthStore } from '../lib/auth.js';
import TotpInput from './TotpInput.jsx';

export default function L2SetupModal({ open, employee, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [qr, setQr] = useState(null);          // { qr_png, otpauth_uri, secret_base32 }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [hint, setHint] = useState(null);       // hiển thị khi cần nhập lại mã mới
  const enabledRef = useRef(false);             // M115: đã commit secret (enable OK) chưa
  const setL2Session = useAuthStore(s => s.setL2Session);

  // Khi mở modal → call /auth/l2/setup để lấy QR
  useEffect(() => {
    if (!open || !employee) return;
    let cancelled = false;
    setStep(1); setCode(''); setErr(null); setQr(null); setHint(null);
    enabledRef.current = false;
    (async () => {
      try {
        const r = await api.totpL2Setup(employee.id);
        if (!cancelled) setQr(r);
      } catch (e) {
        if (!cancelled) {
          // M115: nếu BE bảo NV đã enable (chỉ NV cũ mới setup lại) — đóng + chuyển sang luồng login L2
          if (e.data?.code === 'L2_ALREADY_ENABLED') {
            toast.error('NV này đã thiết lập trước rồi — hãy dùng luồng đăng nhập');
            onClose?.();
            return;
          }
          setErr(e);
          toast.error(e.data?.error || e.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, employee, onClose]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open || !employee) return null;

  // M117 — submit dùng 1 endpoint duy nhất /auth/l2/enable (BE trả session luôn).
  // Fallback verify() chỉ dùng khi BE bảo NV đã enabled từ trước (legacy / race).
  async function submitEnable(e, codeOverride) {
    e?.preventDefault?.();
    const c = codeOverride ?? code;
    if (c.length !== 6 || busy) return;
    setBusy(true);
    setHint(null);
    try {
      let session;
      if (!enabledRef.current) {
        try {
          // M117: enable trả thẳng { l2_session, expires_at, employee } — KHÔNG verify thêm
          session = await api.totpL2Enable(employee.id, c);
          enabledRef.current = true;
        } catch (eEnable) {
          if (eEnable.data?.code === 'L2_ALREADY_ENABLED') {
            // NV đã enable trước đó (legacy hoặc bấm trùng) → fallback verify
            enabledRef.current = true;
          } else if (eEnable.status === 401) {
            // Mã sai — secret CHƯA commit (BE check verifyTotp trước UPDATE)
            setHint('Mã chưa đúng — kiểm tra lại trên Authenticator.');
            toast.error('Mã TOTP chưa đúng');
            setCode('');
            return;
          } else {
            throw eEnable;
          }
        }
      }
      // Fallback path: NV đã enabled từ trước → verify mở session
      if (!session) {
        session = await api.totpL2Verify(employee.id, c);
      }
      setL2Session({
        sessionId: session.l2_session,
        expiresAt: session.expires_at,
        employee: session.employee,
      });
      toast.success(`✓ Đã thiết lập L2 & bắt đầu phiên — chào ${session.employee.full_name}`);
      onSuccess?.(session.employee);
    } catch (e2) {
      if (e2.data?.code === 'L2_ALREADY_ENABLED') {
        enabledRef.current = true;
        setHint('Bạn đã cài đặt rồi — nhập mã hiện tại từ Authenticator để mở phiên.');
        toast('Bạn đã cài đặt — nhập mã mới để mở phiên', { icon: 'ℹ️' });
      } else if (e2.status === 401) {
        setHint('Mã chưa đúng — kiểm tra lại trên Authenticator.');
        toast.error('Mã TOTP chưa đúng');
      } else {
        toast.error(e2.data?.error || e2.message);
      }
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  function copySecret() {
    if (!qr?.secret_base32) return;
    navigator.clipboard.writeText(qr.secret_base32);
    toast.success('Đã copy secret');
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">🔐 Thiết lập TOTP L2</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              NV: <span className="font-semibold">{employee.full_name}</span>
              {employee.phone ? <> · {employee.phone}</> : null}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none"
            disabled={busy}
          >✕</button>
        </div>

        {err && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {err.data?.error || err.message}
          </div>
        )}

        {!err && !qr && (
          <div className="text-center text-slate-500 py-8">Đang tạo QR…</div>
        )}

        {qr && step === 1 && (
          <>
            <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
              <li>Mở <b>Google Authenticator</b> / Authy trên điện thoại của bạn</li>
              <li>Bấm <b>+</b> → <b>Scan QR</b></li>
              <li>Quét mã bên dưới, hoặc nhập secret tay</li>
            </ol>
            <div className="flex justify-center">
              <img
                src={qr.qr_png}
                alt="QR TOTP"
                className="w-56 h-56 border border-slate-200 rounded-lg"
              />
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-500 mb-1">Hoặc nhập tay secret:</div>
              <button
                onClick={copySecret}
                className="font-mono text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 break-all"
                title="Click để copy"
              >
                {qr.secret_base32}
              </button>
            </div>
            <button
              onClick={() => setStep(2)}
              className="w-full py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
            >
              Tôi đã quét xong → Nhập mã 6 số
            </button>
          </>
        )}

        {qr && step === 2 && (
          <form onSubmit={submitEnable} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Mã 6 số từ Authenticator
              </label>
              <TotpInput
                value={code}
                onChange={setCode}
                onSubmit={(c) => submitEnable(null, c)}
                autoFocus
                disabled={busy}
                placeholder="000000"
                testId="l2-setup-totp-input"
              />
            </div>

            {hint && (
              <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                {hint}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={busy}
                className="flex-1 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
              >
                ← Quay lại
              </button>
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="flex-1 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {busy ? 'Đang xử lý…' : 'Xác nhận & Bắt đầu'}
              </button>
            </div>
            <p className="text-[11px] text-slate-400 text-center">
              Đủ 6 số sẽ tự xác nhận — phiên 4 tiếng mở ngay sau đó.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
